import * as React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Loader } from "lucide-react";
import { toast } from "sonner";
import { ReviewTileItem } from "@/types/tile.types";
import { useTileInsert } from "@/hooks/useTileInsert";
import { useTileComment } from "@/hooks/useTileComment";
import { useTileCopy } from "@/hooks/useTileCopy";
import { useTileDismiss } from "@/hooks/useTileDismiss";
import { useSubmitFeedback } from "@/hooks/useSubmitFeedback";

import { useTileReplace } from "@/hooks/useTileReplace";
import { useUpdateTileStatus } from "@/hooks/useUpdateTileStatus";
import { useWriteIt } from "@/hooks/useWriteIt";

import {
  buildTileStatusPayload,
  shouldUpdateTileStatus,
  handleRatingSubmit,
  buildPrimaryActions,
  buildSecondaryActions,
  dispatchTileAction,
} from "./TileActions.utils";

const RatingDialog = React.lazy(() => import("./RatingDialog"));

interface TileActionsProps {
  item: ReviewTileItem;
  documentId: string;
  isBulkOperationInProgress: boolean;
  onProcessingStateChange: (isProcessing: boolean) => void;
}

interface TileActionButtonProps {
  name: string;
  icon: React.ReactNode;
  tooltip: string;
  onClick: () => void;
  isLoading: boolean;
  disabled: boolean;
  className?: string;
}

const TileActionButton: React.FC<TileActionButtonProps> = ({
  name: _name,
  icon,
  tooltip,
  onClick,
  isLoading,
  disabled,
  className,
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          role="button"
          aria-disabled={disabled}
          className={`size-3.5 ${className ?? ""} ${disabled ? "pointer-events-none opacity-50 cursor-not-allowed" : ""}`}
          onClick={disabled ? undefined : onClick}
        >
          {isLoading ? <Loader className="animate-spin" /> : icon}
        </div>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

function useTileHooks(documentId: string, item: ReviewTileItem) {
  const { mutate: updateTileStatus } = useUpdateTileStatus();

  const onSuccessWithStatus = (message: string) => {
    toast.success(message);
    if (shouldUpdateTileStatus(item)) {
      updateTileStatus(buildTileStatusPayload(documentId, item));
    }
  };

  const onError = (error: string) => toast.error(error);

  const insertHook = useTileInsert({
    onSuccess: onSuccessWithStatus,
    onError,
  });

  const commentHook = useTileComment({
    onSuccess: (message) => toast.success(message),
    onError,
  });

  const copyHook = useTileCopy({
    onSuccess: (message) => toast.success(message),
    onError,
  });

  const dismissHook = useTileDismiss({
    onSuccess: (message) => toast.success(message),
    onError,
    documentId: documentId,
  });

  const replaceHook = useTileReplace({
    onSuccess: onSuccessWithStatus,
    onError,
  });

  return { insertHook, commentHook, copyHook, dismissHook, replaceHook };
}

export const TileActions: React.FC<TileActionsProps> = ({
  item,
  documentId,
  isBulkOperationInProgress,
  onProcessingStateChange,
}) => {
  const [showRatingDialog, setShowRatingDialog] = React.useState(false);
  const [isSubmittingRating, setIsSubmittingRating] = React.useState(false);

  const hooks = useTileHooks(documentId, item);
  const { insertHook, commentHook, copyHook, dismissHook, replaceHook } = hooks;

  const { mutateAsync: submitFeedback } = useSubmitFeedback();
  const { mutate: writeIt } = useWriteIt();

  const isItemBeingProcessed =
    insertHook.insertingTileId === item.id ||
    commentHook.addingCommentTileId === item.id ||
    copyHook.copyingTileId === item.id ||
    dismissHook.dismissingTileId === item.id ||
    replaceHook.replacingTileId === item.id;

  React.useEffect(() => {
    onProcessingStateChange(isItemBeingProcessed);
  }, [isItemBeingProcessed, onProcessingStateChange]);

  const isAnyOperationInProgress =
    isBulkOperationInProgress ||
    insertHook.isInserting ||
    commentHook.isAddingComments ||
    copyHook.isCopying ||
    replaceHook.isReplacing ||
    // Prevent other actions while a dismiss (API delete) is in flight
    dismissHook.dismissingTileId === item.id;

  const handleAction = async (action: string) =>
    dispatchTileAction(action, item, documentId, hooks, writeIt, setShowRatingDialog);

  const primary = buildPrimaryActions(item, hooks, handleAction, isAnyOperationInProgress);
  const secondary = buildSecondaryActions(item, hooks, handleAction, isAnyOperationInProgress);
  const visibleActions = [...primary, ...secondary].filter((a) => a.condition ?? false);

  return (
    <div className="flex flex-col items-center gap-4 shrink-0">
      {visibleActions.map((action) => (
        <TileActionButton key={action.name} {...action} />
      ))}

      <React.Suspense fallback={null}>
        <RatingDialog
          isOpen={showRatingDialog}
          onOpenChange={setShowRatingDialog}
          onSubmit={(rating: number, comment: string) =>
            handleRatingSubmit(
              rating,
              comment,
              documentId,
              item,
              submitFeedback,
              setIsSubmittingRating,
              setShowRatingDialog
            )
          }
          isSubmitting={isSubmittingRating}
        />
      </React.Suspense>
    </div>
  );
};
